import { isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';
export interface PropertyCreateBody {
  command_id: string;
  name: string;
  code: string;
}
export interface CreatedProperty {
  id: string;
  name: string;
  code: string;
}
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '지점 등록 결과를 확인하지 못했습니다.',
  });
const record = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);
const exact = (v: Record<string, unknown>, keys: string[]) =>
  Object.keys(v).length === keys.length && keys.every((k) => Object.hasOwn(v, k));
const nullableOptional = (v: Record<string, unknown>, keys: string[]) =>
  Object.keys(v).every((key) => keys.includes(key) && v[key] === null);
// Python str.strip whitespace; FEFF is valid text in Core, unlike JavaScript trim.
// Core rejects C0/C1 controls below; remaining Python whitespace matches White_Space.
const pythonBlank = /^\p{White_Space}*$/u;
export const validNativeName = (v: string) =>
  !pythonBlank.test(v) &&
  Array.from(v).length <= 200 &&
  Array.from(v).every((c) => {
    const n = c.codePointAt(0) ?? 0;
    return n >= 32 && (n < 127 || n > 159);
  });
// Receipt lifetime follows the frozen request owned by the existing actor/epoch lease.
const receipts = new WeakMap<PropertyCreateBody, string>();
export async function createNativeProperty(
  client: ServiceClient,
  body: PropertyCreateBody,
): Promise<void> {
  if (
    !exact(body as unknown as Record<string, unknown>, ['command_id', 'name', 'code']) ||
    !isUuid(body.command_id) ||
    !validNativeName(body.name) ||
    !validNativeName(body.code)
  )
    throw invalid();
  const v = await client.post<unknown>('admin/properties', { json: body, expectedStatus: 201 });
  if (
    !record(v) ||
    !exact(v, [
      'command_id',
      'target_kind',
      'target_id',
      'result_revision',
      'ancestor_revisions',
      'http_status',
    ]) ||
    v.command_id !== body.command_id ||
    v.target_kind !== 'PROPERTY' ||
    !isUuid(v.target_id) ||
    v.result_revision !== 1 ||
    v.http_status !== 201 ||
    !record(v.ancestor_revisions) ||
    !nullableOptional(v.ancestor_revisions, [
      'property_id',
      'property_structure_revision',
      'room_type_id',
      'room_id',
      'room_topology_revision',
      'group_revisions',
    ])
  )
    throw invalid();
  receipts.set(body, v.target_id);
}
export async function readCreatedProperty(
  client: ServiceClient,
  body?: PropertyCreateBody,
): Promise<CreatedProperty | null> {
  if (!body) return null;
  const id = receipts.get(body);
  if (!id) return null;
  const v = await client.get<unknown>(`admin/properties/${id}`, { expectedStatus: 200 });
  if (
    !record(v) ||
    !['id', 'name', 'revision', 'code', 'structure_revision', 'application_enabled'].every((k) =>
      Object.hasOwn(v, k),
    ) ||
    !nullableOptional(
      Object.fromEntries(
        Object.entries(v).filter(
          ([k]) =>
            ![
              'id',
              'name',
              'revision',
              'code',
              'structure_revision',
              'application_enabled',
            ].includes(k),
        ),
      ),
      [
        'property_id',
        'room_type_id',
        'room_id',
        'floor',
        'topology_revision',
        'room_topology_revision',
        'physical_capacity',
        'unit_presentation',
        'specifications',
        'description',
      ],
    ) ||
    v.id !== id ||
    typeof v.name !== 'string' ||
    typeof v.code !== 'string' ||
    !Number.isSafeInteger(v.revision) ||
    Number(v.revision) < 1 ||
    !Number.isSafeInteger(v.structure_revision) ||
    Number(v.structure_revision) < 1 ||
    typeof v.application_enabled !== 'boolean'
  )
    throw invalid();
  return { id, name: v.name, code: v.code };
}
